import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  Link as ChakraLink,
  Icon,
  Divider,
  GridItem,
  Heading,
  SimpleGrid,
  Image,
} from '@chakra-ui/react';
import { FaPhoneAlt, FaEnvelope, FaMapMarkerAlt, FaFacebook, FaInstagram, FaYoutube } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { templeAssets } from '../../config/assets';
import { useSiteConfig } from '../../context/SiteConfigContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../context/AuthContext';
import { useState, useEffect } from 'react';
import api from '../../config/axios';

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

const Footer = () => {
  const { t } = useTranslation();
  const { config } = useSiteConfig();
  const { isAdmin } = useAuth();
  const [visitorStats, setVisitorStats] = useState({ today: 0, total: 0 });

  useEffect(() => {
    const fetchVisitorStats = async () => {
      if (isAdmin()) {
        try {
          const response = await api.get('/api/admin/visitor-stats');
          setVisitorStats(response.data);
        } catch (error) {
          console.error('Error fetching visitor stats for footer:', error);
        }
      }
    };

    fetchVisitorStats();
    // Refresh stats every 5 minutes if on page
    const interval = setInterval(fetchVisitorStats, 300000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Exact colors from reference site
  const gold = '#ecbd00';
  const bgColor = '#1f2937';
  const textColor = '#d1d5db';
  const headingColor = '#ffffff';
  const borderColor = '#374151';

  const footerLinks = {
    quickLinks: [
      { name: t('common.home'), path: '/' },
      { name: t('header.templeHistory'), path: '/temple-history' },
      { name: t('header.gallery'), path: '/gallery' },
      { name: t('header.events'), path: '/events' },
      { name: 'Wall of Donors', path: '/donor-wall' },
      { name: t('common.privacyPolicy') || 'Privacy Policy', path: '/privacy-policy' },
      { name: t('common.termsOfUse') || 'Terms of Use', path: '/terms-of-use' },
    ],
    services: [
      { name: t('header.donate'), path: '/donate' },
      { name: t('header.prayerRequest'), path: '/prayer-request' },
      { name: t('header.templeTimings'), path: '/Abhishekam' },
      { name: t('common.contact'), path: '/contact' },
      { name: t('header.aiAssistant') || 'AI Assistant', href: '#ai-assistant', onClick: (e) => { e?.preventDefault(); window.dispatchEvent(new Event('open-ai-assistant')); } },
    ],
  };

  const socialLinks = [
    { icon: FaFacebook, href: templeAssets.socialMedia.facebook, label: 'Facebook' },
    { icon: FaInstagram, href: templeAssets.socialMedia.instagram, label: 'Instagram' },
    { icon: FaYoutube, href: templeAssets.socialMedia.youtube, label: 'YouTube' },
  ];

  return (
    <MotionBox
      bg={bgColor}
      color={textColor}
      borderTop={`3px solid ${gold}`}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <Container maxW="7xl" py={4}>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {/* Temple Info */}
          <GridItem>
            <MotionVStack
              align="flex-start"
              spacing={4}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Image
                src={config.logoFooter || config.logo || '/images/logo.webp'}
                alt={templeAssets.templeNameShort}
                h="45px"
                w="45px"
                objectFit="contain"
                fallback={
                  <Heading size="md" color={headingColor} fontFamily="Roboto, sans-serif" fontWeight="700">
                    {templeAssets.templeNameShort}
                  </Heading>
                }
              />
              <Text fontSize="sm" lineHeight="1.8" color={textColor} fontFamily="Roboto, sans-serif">
                {t('home.footerDesc')}
              </Text>
              <HStack spacing={3}>
                {socialLinks.map((social, index) => (
                  <ChakraLink
                    key={index}
                    href={social.href}
                    isExternal
                    aria-label={`Follow us on ${social.label}`}
                    _hover={{ color: gold, transform: 'translateY(-2px)' }}
                    transition="all 0.3s"
                  >
                    <Icon as={social.icon} boxSize={5} />
                  </ChakraLink>
                ))}
              </HStack>
            </MotionVStack>
          </GridItem>

          {/* Quick Links */}
          <GridItem>
            <MotionVStack
              align="flex-start"
              spacing={2}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Heading size="xs" color={headingColor} mb={1} fontFamily="Roboto, sans-serif" fontWeight="600" textTransform="uppercase" letterSpacing="1px">
                {t('common.quickLinks') || 'Quick Links'}
              </Heading>
              {footerLinks.quickLinks.map((link, index) => (
                <ChakraLink
                  key={index}
                  as={Link}
                  to={link.path}
                  fontSize="sm"
                  color={textColor}
                  _hover={{
                    color: gold,
                    transform: 'translateX(5px)',
                  }}
                  transition="all 0.2s"
                  fontFamily="Roboto, sans-serif"
                >
                  {link.name}
                </ChakraLink>
              ))}
            </MotionVStack>
          </GridItem>

          {/* Services */}
          <GridItem>
            <MotionVStack
              align="flex-start"
              spacing={2}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <Heading size="xs" color={headingColor} mb={1} fontFamily="Roboto, sans-serif" fontWeight="600" textTransform="uppercase" letterSpacing="1px">
                {t('common.services') || 'Services'}
              </Heading>
              {footerLinks.services.map((link, index) => (
                link.onClick ? (
                  <ChakraLink
                    key={index}
                    href={link.href || '#'}
                    onClick={link.onClick}
                    fontSize="sm"
                    color={textColor}
                    _hover={{
                      color: gold,
                      transform: 'translateX(5px)',
                    }}
                    transition="all 0.2s"
                    fontFamily="Roboto, sans-serif"
                    cursor="pointer"
                  >
                    {link.name}
                  </ChakraLink>
                ) : (
                  <ChakraLink
                    key={index}
                    as={Link}
                    to={link.path}
                    fontSize="sm"
                    color={textColor}
                    _hover={{
                      color: gold,
                      transform: 'translateX(5px)',
                    }}
                    transition="all 0.2s"
                    fontFamily="Roboto, sans-serif"
                  >
                    {link.name}
                  </ChakraLink>
                )
              ))}
            </MotionVStack>
          </GridItem>

          {/* Contact Info */}
          <GridItem>
            <MotionVStack
              align="flex-start"
              spacing={2}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <Heading size="xs" color={headingColor} mb={1} fontFamily="Roboto, sans-serif" fontWeight="600" textTransform="uppercase" letterSpacing="1px">
                {t('common.contact')}
              </Heading>
              <VStack align="flex-start" spacing={2.5}>
                <HStack spacing={3}>
                  <Icon as={FaMapMarkerAlt} color={gold} />
                  <Text fontSize="sm" color={textColor} fontFamily="Roboto, sans-serif">
                    {templeAssets.contact.address}
                  </Text>
                </HStack>
                <HStack spacing={3}>
                  <Icon as={FaPhoneAlt} color={gold} />
                  <Text fontSize="sm" color={textColor} fontFamily="Roboto, sans-serif">
                    {templeAssets.contact.phone}
                  </Text>
                </HStack>
                <HStack spacing={3}>
                  <Icon as={FaEnvelope} color={gold} />
                  <Text fontSize="sm" color={textColor} fontFamily="Roboto, sans-serif">
                    {templeAssets.contact.email}
                  </Text>
                </HStack>
              </VStack>
            </MotionVStack>
          </GridItem>
        </SimpleGrid>

        <Divider borderColor={borderColor} my={3} />

        {/* Copyright */}
        <MotionBox
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <VStack spacing={2}>
            <HStack spacing={3} justify="center" flexWrap="wrap">
              <ChakraLink
                as={Link}
                to="/privacy-policy"
                fontSize="xs"
                color={textColor}
                _hover={{ color: gold }}
                transition="color 0.2s"
              >
                {t('common.privacyPolicy') || 'Privacy Policy'}
              </ChakraLink>
              <Text fontSize="xs" color="gray.500">•</Text>
              <ChakraLink
                as={Link}
                to="/terms-of-use"
                fontSize="xs"
                color={textColor}
                _hover={{ color: gold }}
                transition="color 0.2s"
              >
                {t('common.termsOfUse') || 'Terms of Use'}
              </ChakraLink>
            </HStack>
            <Text fontSize="xs" color={textColor} textAlign="center" fontFamily="Roboto, sans-serif">
              © {new Date().getFullYear()} {templeAssets.templeName}. {t('common.allRightsReserved')}
            </Text>
          </VStack>
        </MotionBox>

        {/* Admin Visitor Stats Bar */}
        {isAdmin() && (
          <Box
            mt={4}
            pt={4}
            borderTop={`1px dashed ${borderColor}`}
            textAlign="center"
          >
            <HStack justify="center" spacing={8}>
              <VStack spacing={0}>
                <Text fontSize="10px" color="gray.500" fontWeight="bold">TOTAL VISITORS</Text>
                <Text fontSize="md" color={gold} fontWeight="bold">
                  {(visitorStats?.total || 0).toLocaleString()}
                </Text>
              </VStack>
              <VStack spacing={0}>
                <Text fontSize="10px" color="gray.500" fontWeight="bold">VISITORS TODAY</Text>
                <Text fontSize="md" color="blue.400" fontWeight="bold">
                  {(visitorStats?.today || 0).toLocaleString()}
                </Text>
              </VStack>
            </HStack>
            <Text mt={2} fontSize="9px" color="gray.600">Admin Statistics View</Text>
          </Box>
        )}
      </Container>
    </MotionBox>
  );
};

export default Footer;
